import {
  isObject, ceil, reduce, orderBy,
  castArray, find, includes, isNil,
  filter as filterArray, parseInt,
  keys, map,
} from 'lodash';
import qs from 'query-string';
import router from '@/router';
import * as Model from '@/models';
import base64 from '@/utils/base64';
import sportIds from '@/services/helpers/sports';
import userRoles from '@/services/helpers/user-roles';
import { formatPlayerName } from '@/services/helpers/player-setup-mapper';
import { validateInferModalMarkets } from '@/components/player-setup/infer/infer-payload-helper';

const { DPS_TRADER } = userRoles;
const {
  BASKETBALL_ID,
  FOOTBALL_ID,
  SOCCER_ID,
  ULTIMATE_ID,
  HOCKEY_ID,
  BASEBALL_ID,
  TENNIS_ID,
} = sportIds;

export default {
  latestRoute: (state) => state.latestRoute,
  columns: (state) => orderBy(reduce(
    state.columns,
    (fields, values, key) => [
      ...fields,
      {
        ...values,
        key,
      },
    ],
    [],
  ), 'order'),
  search: (state) => state.search,
  specialIdSearch: (state) => state.specialIdSearch,
  selectedRowSpacingOption: (state) => state.selectedRowSpacingOption,
  currentPage: () => parseInt(router.currentRoute.value.query.page || 1, 10),
  rowsPerPage: () => {
    const defaultRowsPerPage = includes(router.currentRoute.value.name, 'Mapping') ? '100' : '50';
    return parseInt(router.currentRoute.value.query.rowsPerPage || defaultRowsPerPage, 10);
  },
  totalRowsCount: (state) => state.totalRowsCount,
  lastPage: (state, getters) => ceil(state.totalRowsCount / getters.rowsPerPage) || 1,
  operatorsList: (state) => state.operatorsList,

  eventListFields: (state) => orderBy(reduce(
    state.columns,
    (fields, values, key) => [
      ...fields,
      {
        ...values,
        key,
      },
    ],
    [],
  ), 'order'),
  eventListFilters: (state) => reduce(
    state.columns,
    (fields, values, key) => {
      if (values.filterable === false) return fields;
      return [
        ...fields,
        {
          ...values,
          field: key,
          inputType: values.inputType || 'text',
          inputArgs: values.inputArgs || null,
        },
      ];
    },
    [],
  ),
  eventListAppliedFilters: (state) => {
    const path = router.currentRoute.value.fullPath;
    const parsedUrl = qs.parseUrl(path);
    const filterParams = castArray(parsedUrl.query?.filter || []);
    return reduce(
      filterParams,
      (validFilters, filterParam) => {
        const filter = base64.decodeAndParse(filterParam);
        const model = state.columns[filter.field || ''];

        // Field validation
        if (!model) return validFilters;
        // Modifier validation
        if (isNil(filter.modifier)) return validFilters;
        if (!find(state.eventListFilterModifiers?.[model.inputType] || [], { value: filter.modifier })) return validFilters;
        // Value validation
        if (isNil(filter.value)) return validFilters;
        if (model.inputType === 'select' && !find(model.inputArgs, { value: filter.value[filter.value.length - 1] })) return validFilters;
        if (model.inputType === 'date' && !/^\d{4}-\d{2}-\d{2}$/.test(filter.value[0]) && !/^\d{4}-\d{2}-\d{2}$/.test(filter.value[1])) return validFilters;

        return [...validFilters, filter];
      },
      [],
    );
  },
  eventListFilterModifiers: (state) => state.eventListFilterModifiers,
  eventListAppliedSort: () => {
    const path = router.currentRoute.value.fullPath;
    const parsedUrl = qs.parseUrl(path);
    let appliedSort = parsedUrl.query?.sort;
    if (!appliedSort) {
      return {
        field: 'startsAt',
        order: 'ASC',
      };
    }
    appliedSort = base64.decodeAndParse(appliedSort);
    return appliedSort;
  },
  previousEventListOptions: (state) => state.previousEventListOptions,
  eventList: (state) => state.eventList,
  eventListLoading: (state) => state.eventListLoading,
  specialList: (state) => state.specialList,
  specialListLoading: (state) => state.specialListLoading,
  userList: (state) => state.userList,
  userListLoading: (state) => state.userListLoading,
  userAuditLog: (state) => state.userAuditLog,
  userAuditLogLoading: (state) => state.userAuditLogLoading,
  suspendSpecialActionLoading: (state) => (specialId) => !!filterArray(
    state.suspensionInProgressSpecialIds,
    (id) => id === specialId,
  )?.length,
  bookedEvents: (state) => [
    ...reduce(
      state.eventList,
      (bookedEvents, event) => {
        if (!event.booked) return bookedEvents;
        return [...bookedEvents, event.eventId];
      },
      [],
    ),
    ...state.bookedEventsInSession,
  ],
  selectedEvents: (state) => state.selectedEvents,
  selectedMarkets: (state) => state.selectedMarkets,
  isEventsSidebarActive: (state) => state.isEventsSidebarActive,
  isEventsFiltersActive: (state) => state.isEventsFiltersActive,

  sidebarExpanded: (state) => state.sidebar.expanded,
  sidebarLoading: (state) => state.sidebar.loading,
  sidebarSports: (state) => state.sidebar.sports,
  sidebarRegions: (state) => state.sidebar.regions,
  sidebarRegionsBySportId: (state) => (sportId) => filterArray(state.sidebar.regions, { sportId }),
  sidebarCompetitions: (state) => state.sidebar.competitions,
  sidebarCompetitionsBySportIdAndRegionId: (state) => (sportId, regionId) => {
    if (!sportId && !regionId) return state.sidebar.competitions;
    const competitionsBySport = filterArray(state.sidebar.competitions, { sportId });
    if (!regionId) return competitionsBySport;
    return filterArray(competitionsBySport, { regionId });
  },
  sidebarLoadingIds: (state) => state.sidebar.loadingIds,
  sidebarLoadingCompetitions: (state) => (sportId, regionId) => includes(state.sidebar.loadingIds, sportId) && includes(state.sidebar.loadingIds, regionId),
  sidebarSelection: (state) => state.sidebar.selection,
  sidebarSportId: (state, getters) => getters.sidebarSelection?.sportId,
  sidebarRegionId: (state, getters) => getters.sidebarSelection?.regionId,
  sidebarCompetitionId: (state, getters) => getters.sidebarSelection?.competitionId,
  sidebarSubscribed: (state, getters) => getters.sidebarSelection?.subscribed,

  subscriptionModalOpen: (state) => state.subscriptionModal.open,
  subscriptionModalType: (state) => state.subscriptionModal.type,
  subscriptionModalId: (state) => state.subscriptionModal.id,
  bulkSubscriptionModalOpen: (state) => state.bulkSubscriptionModal.open,

  event: (state) => {
    if (!isObject(state.event)) return null;
    switch (state.event?.sportId) {
    case SOCCER_ID:
      return Model.SoccerEvent(state.event);
    case BASKETBALL_ID:
      return Model.BasketballEvent(state.event);
    case FOOTBALL_ID:
      return Model.FootballEvent(state.event);
    case ULTIMATE_ID:
      return Model.UltimateEvent(state.event);
    case HOCKEY_ID:
      return Model.HockeyEvent(state.event);
    case BASEBALL_ID:
      return Model.BaseballEvent(state.event);
    case TENNIS_ID:
      return Model.TennisEvent(state.event);
    default:
      return Model.Event(state.event);
    }
  },
  eventLoading: (state) => state.eventLoading,
  eventLoaded: (state) => !state.eventLoading && state.event,
  eventMarketsLoading: (state) => state.eventMarketsLoading,
  eventMarketsLoaded: (state) => !state.eventMarketsLoading && state.eventMarkets,
  eventMarkets: (state, getters) => {
    if (
      !getters.eventLoaded
      || !getters.eventMarketsLoaded
      || !state.eventMarkets
    ) return null;

    if (getters.event.matchState === 'FINISHED') return [];
    const markets = getters.event.matchState === 'LIVE'
      ? filterArray(state.eventMarkets, { inPlay: true })
      : state.eventMarkets;
    return markets;
  },
  eventTeamPlayers: (state) => state.eventTeamPlayers,

  marketTemplate: (state) => state.marketTemplate,
  sportTemplates: (state) => state.sportTemplates,
  competitionTemplateChanged: (state) => state.competitionTemplateChanged,
  userToken: (state) => state.userToken,
  authReady: (state) => state.authReady,
  isAuthenticated: (state) => state.authReady && !!state.userToken,
  userRoles: (state) => state.userRoles,
  operator: (state) => state.operator,
  isDpsTraderRole: (state) => state.userRoles.includes(DPS_TRADER),
  loginErrorMessage: (state) => state.loginErrorMessage,
  changePasswordAttributes: (state) => state.changePasswordAttributes,

  allFeeds: (state) => state.allFeeds,

  notifications: (state) => state.notifications,
  suspendEventActionLoading: (state) => (eventId) => !!filterArray(state.suspensionInProgressEventIds, (stateEventId) => stateEventId === eventId)?.length,
  suspendMarketTypeActionLoading: (state) => state.suspendMarketTypeActionLoading,
  eventListMappingEvent: (state) => state.eventListMappingEvent,

  marketGroupsBySportId: (state) => (sportId) => filterArray(state.marketGroups,
    (group) => sportId === group.sportId && group.active),
  marketFavorites: (state) => state.marketFavorites,

  mappingInitialized: (state) => state.mapping.initialized,
  mappingLoading: (state) => state.mapping.loading,
  mappingSports: (state) => state.mapping.sports,
  mappingEntries: (state) => state.mapping.entries,
  mappingSelectedSportId: (state) => state.mapping.selectedSportId,
  mappingSelectedSportLabel: (state) => state.mapping.selectedSportLabel,
  mappingPrimaryFeeds: (state) => {
    const routeName = router.currentRoute.value?.name;
    const feeds = routeName === 'regionsMapping'
      ? state.mapping.primaryFeeds
      : filterArray(state.mapping.primaryFeeds, (feed) => includes(state.mapping.feedsPerSport, feed));
    return feeds;
  },
  mappingSecondaryFeeds: (state) => {
    const routeName = router.currentRoute.value?.name;
    const feeds = routeName === 'regionsMapping'
      ? filterArray(state.mapping.secondaryFeeds, (feed) => feed !== 'nba.com' && feed !== 'rotowire')
      : filterArray(state.mapping.secondaryFeeds, (feed) => includes(state.mapping.feedsPerSport, feed));
    return feeds;
  },
  mappingDisplayedFeeds: (state) => state.mapping.displayedFeeds,
  mappingSelectedFeed: (state) => state.mapping.selectedFeed,
  mappingSearchOptions: (state) => state.mapping.searchOptions,
  mappingSelectedSearchOption: (state) => state.mapping.selectedSearchOption,
  mappingSearchQuery: (state) => state.mapping.searchQuery,
  mappingSelectedDisplay: (state) => state.mapping.selectedDisplay,
  mappingDisplayOptions: (state) => state.mapping.displayOptions,
  mappingSelectedDate: (state) => state.mapping.selectedDate,
  mappingDateOptions: (state) => state.mapping.dateOptions,
  eventSuggestedMappings: (state) => state.eventSuggestedMappings,
  competitorSuggestedMappings: (state) => state.competitorSuggestedMappings,
  regionSuggestedMappings: (state) => state.regionSuggestedMappings,
  competitionSuggestedMappings: (state) => state.competitionSuggestedMappings,
  playerSuggestedMappings: (state) => state.playerSuggestedMappings,
  manualResultingEvent: (state) => state.manualResultingEvent,
  manualResultingEventFormatted: (state) => (feed) => {
    if (!isObject(state.manualResultingEvent)) return null;
    switch (state.manualResultingEvent?.sportId) {
    case SOCCER_ID:
      return Model.SoccerEvent(state.manualResultingEvent, feed);
    case BASKETBALL_ID:
      return Model.BasketballEvent(state.manualResultingEvent, feed);
    case FOOTBALL_ID:
      return Model.FootballEvent(state.manualResultingEvent, feed);
    case ULTIMATE_ID:
      return Model.UltimateEvent(state.manualResultingEvent, feed);
    case HOCKEY_ID:
      return Model.HockeyEvent(state.manualResultingEvent, feed);
    case BASEBALL_ID:
      return Model.BaseballEvent(state.manualResultingEvent, feed);
    case TENNIS_ID:
      return Model.TennisEvent(state.manualResultingEvent, feed);
    default:
      return Model.Event(state.manualResultingEvent, feed);
    }
  },
  manualResultingAvailableFeeds: (state) => {
    if (!isObject(state.manualResultingEvent)) return null;
    switch (state.manualResultingEvent?.sportId) {
    case SOCCER_ID:
      return map(state.manualResultingEvent?.soccerEventDetails?.nodes, 'source');
    case BASKETBALL_ID:
      return map(state.manualResultingEvent?.basketballEventDetails?.nodes, 'source');
    case FOOTBALL_ID:
      return map(state.manualResultingEvent?.footballEventDetails?.nodes, 'source');
    case ULTIMATE_ID:
      return map(state.manualResultingEvent?.ultimateEventDetails?.nodes, 'source');
    case HOCKEY_ID:
      return map(state.manualResultingEvent?.hockeyEventDetails?.nodes, 'source');
    case BASEBALL_ID:
      return map(state.manualResultingEvent?.baseballEventDetails?.nodes, 'source');
    case TENNIS_ID:
      return map(state.manualResultingEvent?.tennisEventDetails?.nodes, 'source');
    default:
      return null;
    }
  },
  manualResultingEventLoading: (state) => state.manualResultingEventLoading,
  manualResultingEvents: (state) => state.manualResultingEvents,
  manualResultingEventMarkets: (state) => state.manualResultingEventMarkets,
  getManualResultingEventMarketById: (state) => (marketId) => state.manualResultingEventMarkets[marketId],
  manualResultingEventMarketsLoading: (state) => state.manualResultingEventMarketsLoading,
  manualEventState: (state) => state.manualEventState,
  manualEventStateLoading: (state) => state.manualEventStateLoading,
  manualResultingSelectedMarkets: (state) => state.manualResultingSelectedMarkets,
  // Player setup
  playerSetupTableData: (state) => state.playerSetupTableData,
  allPlayerSetupTableDataPlayersNames: (state) => {
    const teamAPlayers = state.playerSetupTableData?.teamA || [];
    const teamBPlayers = state.playerSetupTableData?.teamB || [];
    return reduce(
      [...teamAPlayers, ...teamBPlayers],
      (allPlayers, player) => ({
        ...allPlayers,
        [player.playerIndex]: formatPlayerName(player.playerName),
      }), {},
    );
  },
  playerSetupOriginalTableData: (state) => state.playerSetupOriginalTableData,
  playerParamsPrematchTableData: (state) => state.playerParamsPrematchTableData,
  playerSetupData: (state) => state.playerSetupData,
  isPlayerSetupDataLoading: (state) => state.isPlayerSetupDataLoading,
  isPlayerSetupProjectionsAvailable: (state) => state.isPlayerSetupProjectionsAvailable,
  // Paramfinding data getters
  eventParamfindingData: (state) => state.eventParamfindingData,
  playerParamfindingData: (state) => state.playerParamfindingData,
  isParamfindingDataLoading: (state) => state.isParamfindingDataLoading,
  paramsManagerGameProjectionsAvailable: (state) => state.paramsManagerGameProjectionsAvailable,
  playerSetupDataLoaded: (state) => !state.isPlayerSetupDataLoading && state.playerSetupData,
  isPlayerSetupScoreboardActive: (state) => state.isPlayerSetupScoreboardActive,
  playerSetupEventList: (state) => state.playerSetupEventList,
  playerSetupEventListLoading: (state) => state.playerSetupEventListLoading,
  playerSetupEventSearchActive: (state) => state.playerSetupEventSearchActive,
  teamSquadListLoading: (state) => state.teamSquadListLoading,
  isPlayerSetupSubmitEnabled: (state) => state.isPlayerSetupSubmitEnabled,
  isPlayerSetupSubmitLoading: (state) => state.isPlayerSetupSubmitLoading,
  isPlayerSetupSimulateEnabled: (state) => state.isPlayerSetupSimulateEnabled,
  isPlayerSetupSimulateBySocketChangeEnabled: (state) => state.isPlayerSetupSimulateBySocketChangeEnabled,
  numberOfSimulateAttempts: (state) => state.numberOfSimulateAttempts,
  isPlayerSetupPageFrozen: (state) => state.isPlayerSetupPageFrozen,
  frozenPlayerSetupPageMessage: (state) => state.frozenPlayerSetupPageMessage,
  isPlayerSetupPageSimulateBtnFrozen: (state) => state.isPlayerSetupPageSimulateBtnFrozen,
  availableMarkets: (state) => state.availableMarkets,
  availableComboMarkets: (state) => state.availableComboMarkets,
  isMarketsLoading: (state) => state.isMarketsLoading,
  playersForMarketsPopupList: (state) => state.playersForMarketsPopupList,
  highlightedPlayer: (state) => state.highlightedPlayer,
  playerSetupInferActive: (state) => state.playerSetupInferActive,
  playerSetupBeforeInferState: (state) => state.playerSetupBeforeInferState,
  playerParamsSelectedMode: (state) => state.playerParamsSelectedMode,
  playerParamsLastSubmittedSelectedMode: (state) => state.playerParamsLastSubmittedSelectedMode,
  paramsManagerMarkets: (state) => state.paramsManagerMarkets,
  paramsManagerMarketsBeforeInferState: (state) => state.paramsManagerMarketsBeforeInferState,
  isInferModalOpen: (state) => state.isInferModalOpen,
  lastInferInfo: (state) => state.lastInferInfo,
  inferInfoByEventId: (state) => state.inferInfoByEventId,
  getEventInferInfo: (state) => (eventId) => state.inferInfoByEventId[eventId] || { status: 'N_A' },
  isEventAsyncInferRunning: (state) => (eventId) => includes(
    ['PENDING', 'RUNNING'],
    state.inferInfoByEventId[eventId]?.status,
  ),
  inferAvailable: (state) => state.inferAvailable,
  inferAvailableMarkets: (state) => state.inferAvailableMarkets,
  isAsyncInferRunning: (state) => {
    const eventId = state.playerSetupData?.eventId;
    const inferInfo = eventId
      ? state.inferInfoByEventId[eventId]
      : state.lastInferInfo;
    return includes(['PENDING', 'RUNNING'], inferInfo?.status);
  },
  canApplyBaseballInferResults: (state, getters) => {
    const eventId = state.playerSetupData?.eventId;
    const inferInfo = eventId
      ? getters.getEventInferInfo(eventId)
      : getters.lastInferInfo;
    return inferInfo?.status === 'FINISHED'
      && !getters.isAsyncInferRunning
      && !!inferInfo?.paramFindId
      && state.playerSetupData?.sportId === BASEBALL_ID;
  },
  isInferModalSubmitEnabled: (state) => validateInferModalMarkets({
    availableMarkets: state.inferAvailableMarkets,
    paramsManagerMarkets: state.paramsManagerMarkets,
    playerSetupTableData: state.playerSetupTableData,
  }),
  lineupPresetsByTeam: (state) => state.lineupPresetsByTeam,
  selectedLineupPresetByTeam: (state) => state.selectedLineupPresetByTeam,
  paramsManagerSquadData: (state) => state.paramsManagerSquadData,
  paramsManagerEventSuspendLoading: (state) => state.paramsManagerEventSuspendLoading,
  availableMarketsPlayerTypes: (state) => state.availableMarketsPlayerTypes,
  playerSetupUnpublishedChanges: (state) => state.playerSetupUnpublishedChanges,
  playerSuspendToggleDisabled: (state) => state.playerSuspendToggleDisabled,
  paramsManagerAvailableGameMarkets: (state) => state.paramsManagerAvailableGameMarkets,

  playerSetupMappedPlayersForProjections: (state) => state.playerSetupMappedPlayersForProjections,
  // Game params
  playerSetupGameParams: (state) => state.playerSetupGameParams,
  playerSetupPrematchGameParams: (state) => (eventId) => state.playerSetupPrematchGameParams[eventId] || null,
  gameParamsSelectedMode: (state) => state.gameParamsSelectedMode,
  gameParamsLastSubmittedSelectedMode: (state) => state.gameParamsLastSubmittedSelectedMode,
  allBaseballParks: (state) => state.allBaseballParks,
  gameParamsSelectedMarkets: (state) => state.gameParamsSelectedMarkets,
  gameParamsSelectedMarketsProcessing: (state) => state.gameParamsSelectedMarketsProcessing,
  isPrematchParamsPreviewActive: (state) => (eventId) => state.isPrematchParamsPreviewActive[eventId] || false,
  isPrematchParamsPreviewActiveObject: (state) => state.isPrematchParamsPreviewActive,
  riskFlagsOverride: (state) => state.riskFlagsOverride,
  isEventStaleCheckEnabled: (state) => state.isEventStaleCheckEnabled,

  // Trading UI
  tradingEvent: (state) => state.tradingEvent,
  tradingEventLoading: (state) => state.tradingEventLoading,
  originalTradingPlayerProps: (state) => state.originalTradingPlayerProps,
  lastSimulateTradingPlayerProps: (state) => state.lastSimulateTradingPlayerProps,
  tradingPlayerProps: (state) => state.tradingPlayerProps,
  allTradingEventPlayerPropsNames: (state) => {
    const teamAPlayers = state.tradingPlayerProps?.teamA?.playerProps || [];
    const teamBPlayers = state.tradingPlayerProps?.teamB?.playerProps || [];
    return reduce(
      [...teamAPlayers, ...teamBPlayers],
      (allPlayers, player) => ({
        ...allPlayers,
        [player.playerIndex]: formatPlayerName(player.playerName),
      }), {},
    );
  },
  isSimulateBtnEnabled: (state) => state.isSimulateBtnEnabled,
  isSimulateBtnLoading: (state) => state.isSimulateBtnLoading,
  isPublishBtnEnabled: (state) => state.isPublishBtnEnabled,
  isPublishBtnLoading: (state) => state.isPublishBtnLoading,
  publishDisabledHintMessage: (state) => state.publishDisabledHintMessage,
  tradingPlayerPropsLoading: (state) => state.tradingPlayerPropsLoading,
  tradingEventList: (state) => state.tradingEventList,
  tradingEventListLoading: (state) => state.tradingEventListLoading,
  tradingEventSearchActive: (state) => state.tradingEventSearchActive,
  tradingEventMarkets: (state) => state.tradingEventMarkets,
  getTradingMarketById: (state) => (marketId) => state.tradingEventMarkets[marketId],
  tradingEventSuspendLoading: (state) => state.tradingEventSuspendLoading,
  selectedTradingMarkets: (state) => state.selectedTradingMarkets,
  isTradingUIPageFrozen: (state) => state.isTradingUIPageFrozen,
  isTradingUIPageSimulateBtnFrozen: (state) => state.isTradingUIPageSimulateBtnFrozen,
  isUnsavedChangesActive: (state) => state.isUnsavedChangesActive,
  tradingSquad: (state) => state.tradingSquad,
  tradingGameParamsLoading: (state) => state.tradingGameParamsLoading,
  tradingEventFeeds: (state) => state.tradingEventFeeds,
  tradingEventSelectedFeed: (state) => state.tradingEventSelectedFeed,
  tradingSimulateBySocketChangeEnabled: (state) => state.tradingSimulateBySocketChangeEnabled,

  oddFormats: (state) => state.oddFormats,
  selectedOddFormat: (state) => state.selectedOddFormat,

  // Manual edit
  manualEditSelectedSport: (state) => state.manualEditSelectedSport,
  manualEditTableColumns: (state) => orderBy(reduce(
    state.manualEditTableColumns,
    (fields, values, key) => [
      ...fields,
      {
        ...values,
        key,
      },
    ],
    [],
  ), 'order'),
  manualEditTableData: (state) => state.manualEditTableData,
  manualEditSelectedRow: (state) => state.manualEditSelectedRow,
  manualEditDataLoading: (state) => state.manualEditDataLoading,
  manualEditPlayerSearchList: (state) => state.manualEditPlayerSearchList,
  manualEditPlayerSearchListLoading: (state) => state.manualEditPlayerSearchListLoading,
  manualEditSearch: (state) => state.manualEditSearch,
  manualEditSelectedFeed: (state) => state.manualEditSelectedFeed,
  manualEditIsCreateNewRecordActive: (state) => state.manualEditIsCreateNewRecordActive,
  manualEditTeamSearchList: (state) => state.manualEditTeamSearchList,
  manualEditTeamSearchListLoading: (state) => state.manualEditTeamSearchListLoading,
  // market display configuration
  displayMarketsConfiguration: (state) => state.displayMarketsConfiguration || null,
  getSingleMarketDisplayConfiguration: (state) => (marketCode, selections) => state.displayMarketsConfiguration[`${marketCode}_${selections}`] || null,
  allMarketParameters: (state) => (sportLabel = '') => filterArray(state.allMarketParameters, (parameter) => parameter.sport === sportLabel),

  // Market group
  marketGroupMarketCodeSearchList: (state) => state.marketGroupMarketCodeSearchList,
  marketGroupMarketCodeSearchListLoading: (state) => state.marketGroupMarketCodeSearchListLoading,
  // Multiview
  multiviewListLatestOptions: (state) => state.multiviewList.latestOptions,
  multiviewListLoading: (state) => state.multiviewList.loading,
  multiviewListEventIds: (state) => keys(state.multiviewList.eventsById),
  multiviewListEventsById: (state) => state.multiviewList.eventsById,
  multiviewListCompetitionIds: (state) => keys(state.multiviewList.competitionsById),
  multiviewListCompetitionsById: (state) => state.multiviewList.competitionsById,
  getMultiviewListEventMarkets: (state) => (eventId) => filterArray(state.multiviewList.markets, (_, key) => key.split('_')[0] === eventId),
  getMultiviewListEventMarketsForSimulation: (state) => (eventId) => state.multiviewList?.eventsById[eventId]?.simulatedMarkets,
  getMultiviewListMarketById: (state) => (eventId, marketId) => state.multiviewList.markets[`${eventId}_${marketId}`],
  multiviewDrawerEvent: (state) => state.multiviewDrawer.event,
  multiviewDrawerLoading: (state) => state.multiviewDrawer.loading,
  multiviewDrawerProcessing: (state) => state.multiviewDrawer.processing,
  multiviewOriginalPlayerParams: (state) => state.multiviewOriginalPlayerParams,
  multiviewListParamsLoading: (state) => state.multiviewList.paramsLoading,
  getSingleMultiviewListEvent: (state) => (eventId) => state.multiviewList.eventsById[eventId] || {},

  // Multiview player params
  multiviewPlayerParams: (state) => state.multiviewPlayerParams,
  allMultiviewEventPlayerPropsNames: (state) => {
    const teamAPlayers = state.multiviewPlayerParams?.teamA?.playerProps || [];
    const teamBPlayers = state.multiviewPlayerParams?.teamB?.playerProps || [];
    return reduce(
      [...teamAPlayers, ...teamBPlayers],
      (allPlayers, player) => ({
        ...allPlayers,
        [player.playerIndex]: formatPlayerName(player.playerName),
      }), {},
    );
  },
  multiviewPlayerParamsLoading: (state) => state.multiviewPlayerParamsLoading,
  multiviewLastSimulatePlayerParams: (state) => state.multiviewLastSimulatePlayerParams,
  multiviewSquad: (state) => state.multiviewSquad,
  multiviewSimulateBtnEnabled: (state) => state.multiviewSimulateBtnEnabled,
  multiviewSimulateBtnFrozen: (state) => state.multiviewSimulateBtnFrozen,
  multiviewSimulateBtnLoading: (state) => state.multiviewSimulateBtnLoading,
  multiviewSubmitBtnEnabled: (state) => state.multiviewSubmitBtnEnabled,
  multiviewSubmitBtnLoading: (state) => state.multiviewSubmitBtnLoading,
  multiviewDrawerFrozen: (state) => state.multiviewDrawerFrozen,
  multiviewSimulateBySocketChangeEnabled: (state) => state.multiviewSimulateBySocketChangeEnabled,

  // Odds checker
  oddsCheckerSports: (state) => state.oddsCheckerFiltersMeta.sports,
  oddsCheckerCompetitions: (state) => state.oddsCheckerFiltersMeta.competitions,
  oddsCheckerEvents: (state) => state.oddsCheckerEvents,
  oddsCheckerMarketsByEventId: (state) => (eventId) => filterArray(state.oddsCheckerMarkets, (_, key) => key.split('_')[0] === eventId),
  oddsCheckerEventById: (state) => (eventId) => state.oddsCheckerEvents[eventId],
  oddsCheckerMarketById: (state) => (eventId, marketId) => state.oddsCheckerMarkets[`${eventId}_${marketId}`],
  oddsCheckerEventsLoading: (state) => state.oddsCheckerEventsLoading,
  oddsCheckerQueryOptions: (state) => state.oddsCheckerQueryOptions,
  selectedOddsCheckerMarket: (state) => state.selectedOddsCheckerMarket,

  // Audit log
  auditLogEvent: (state) => state.auditLogEvent,
  auditLogEventLoading: (state) => state.auditLogEventLoading,
  auditLogEventMarkets: (state) => state.auditLogEventMarkets,
  auditLogEventMarketSelectionsUrls: (state) => state.auditLogEventMarketSelectionsUrls,
  auditLogMarketHistoryDashboardLoading: (state) => state.auditLogMarketHistoryDashboardLoading,

  // Market templates
  marketTemplateMarketsScrollPosition: (state) => state.marketTemplateMarketsScrollPosition,

  // Competitions subscriptions
  selectedCompetitionSubscriptionFilter: (state) => state.competitionsSubscriptionsFilters.subscription,
  selectedCompetitionMappingFilter: (state) => state.competitionsSubscriptionsFilters.mapping,

  // Client settings
  clientSettingsModalOpened: (state) => state.clientSettingsModalOpened,

  // User config
  userConfigurationBookmakers: (state) => state.userConfigurationBookmakers || [],
  userConfigurationBetTickerColumns: (state) => state.userConfigurationBetTickerColumns,

  // Socket reconnected modal
  socketReconnectedModalActive: (state) => state.socketReconnectedModalActive,

  // Competition configuration
  competitionConfigurationList: (state) => state.competitionConfigurationList,

  // Metadata
  marketSelectionsBySportId: (state) => (sportId) => state.metadata.marketSelections[sportId] || {},
};
